import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const GlobalErrorContext = createContext(null);

export function GlobalErrorProvider({ children }) {
  const { t } = useTranslation();
  const [errors, setErrors] = useState([]);
  const dismissTimers = useRef(new Map());

  const dismissError = useCallback((id) => {
    const timer = dismissTimers.current.get(id);
    if (dismissTimers.current.has(id)) {
      clearTimeout(timer);
      dismissTimers.current.delete(id);
    }
    setErrors(prev => prev.filter(e => e.id !== id));
  }, []);

  const addError = useCallback((error, options = {}) => {
    const errorObj = {
      id: Date.now() + Math.random(),
      message: error?.message || error || t('errors.genericError'),
      type: options.type || 'error',
      timeout: options.timeout !== undefined ? options.timeout : 5000,
      details: options.details,
      timestamp: new Date().toISOString(),
    };

    setErrors(prev => [...prev, errorObj]);

    if (errorObj.timeout > 0) {
      const timer = setTimeout(() => {
        dismissTimers.current.delete(errorObj.id);
        dismissError(errorObj.id);
      }, errorObj.timeout);
      dismissTimers.current.set(errorObj.id, timer);
    }

    return errorObj.id;
  }, [dismissError, t]);

  const clearAllErrors = useCallback(() => {
    dismissTimers.current.forEach(clearTimeout);
    dismissTimers.current.clear();
    setErrors([]);
  }, []);

  useEffect(() => () => {
    dismissTimers.current.forEach(clearTimeout);
    dismissTimers.current.clear();
  }, []);

  return (
    <GlobalErrorContext.Provider value={{ addError, dismissError, clearAllErrors, errors }}>
      {children}
      <GlobalErrorBanner />
    </GlobalErrorContext.Provider>
  );
}

export function useGlobalError() {
  const context = useContext(GlobalErrorContext);
  if (!context) {
    throw new Error('useGlobalError must be used within GlobalErrorProvider');
  }
  return context;
}

function GlobalErrorBanner() {
  const { t } = useTranslation();
  const { errors, dismissError } = useGlobalError();

  if (errors.length === 0) return null;

  return (
    <div className="global-error-container">
      {errors.map((error) => (
        <div
          key={error.id}
          className={`global-error-banner global-error-banner--${error.type}`}
          role={error.type === 'error' || error.type === 'warning' ? 'alert' : 'status'}
          aria-atomic="true"
        >
          <div className="global-error-body">
            <div className="global-error-content">
              <span className="global-error-icon" aria-hidden="true">
                {error.type === 'success' ? '✓' : error.type === 'warning' ? '⚠' : '✕'}
              </span>
              <span className="global-error-message">{error.message}</span>
            </div>
            {error.details && (
              <details className="global-error-details">
                <summary>{t('common.details')}</summary>
                <pre>{typeof error.details === 'object' ? JSON.stringify(error.details, null, 2) : error.details}</pre>
              </details>
            )}
          </div>
          <button
            className="global-error-dismiss"
            onClick={() => dismissError(error.id)}
            aria-label={t('common.dismiss')}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

export default GlobalErrorProvider;
